import React, { useEffect, useCallback, useState } from 'react';
import PropTypes from 'prop-types';

import './ShareLinksDialog.scss';

const ShareLinksDialog = ({
  isOpen,
  onClose,
  getComponent,
  backendSpecsActions,
  backendSpecsSelectors,
}) => {
  const [selectedSpecId, setSelectedSpecId] = useState('');
  const [shareLinks, setShareLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [newLabel, setNewLabel] = useState('');
  const [newExpiresAt, setNewExpiresAt] = useState('');
  const [creating, setCreating] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [corsOrigins, setCorsOrigins] = useState('');
  const [corsLoading, setCorsLoading] = useState(false);
  const [corsSaving, setCorsSaving] = useState(false);
  const [corsExpanded, setCorsExpanded] = useState(false);

  const Modal = getComponent('Modal', true);
  const ModalHeader = getComponent('ModalHeader', true);
  const ModalTitle = getComponent('ModalTitle', true);
  const ModalBody = getComponent('ModalBody', true);
  const ModalFooter = getComponent('ModalFooter', true);

  const specsList = backendSpecsSelectors?.selectSpecsList?.() || [];

  useEffect(() => {
    if (isOpen && backendSpecsActions?.fetchSpecsList) {
      backendSpecsActions.fetchSpecsList();
    }
  }, [isOpen, backendSpecsActions]);

  const loadShareLinks = useCallback(async (specId) => {
    if (!specId || !backendSpecsActions?.fetchShareLinks) return;
    setLoading(true);
    setError(null);
    try {
      const data = await backendSpecsActions.fetchShareLinks(Number(specId));
      setShareLinks(data || []);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load share links');
      setShareLinks([]);
    } finally {
      setLoading(false);
    }
  }, [backendSpecsActions]);

  const loadCorsConfig = useCallback(async (specId) => {
    if (!specId || !backendSpecsActions?.fetchSpecCors) return;
    setCorsLoading(true);
    try {
      const data = await backendSpecsActions.fetchSpecCors(Number(specId));
      setCorsOrigins((data.allowedOrigins || []).join('\n'));
    } catch (err) {
      setCorsOrigins('');
    } finally {
      setCorsLoading(false);
    }
  }, [backendSpecsActions]);

  const handleSaveCors = useCallback(async () => {
    if (!selectedSpecId || !backendSpecsActions?.updateSpecCors) return;
    setCorsSaving(true);
    setError(null);
    try {
      const origins = corsOrigins
        .split('\n')
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
      const data = await backendSpecsActions.updateSpecCors(Number(selectedSpecId), origins);
      setCorsOrigins((data.allowedOrigins || []).join('\n'));
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to update CORS settings');
    } finally {
      setCorsSaving(false);
    }
  }, [selectedSpecId, corsOrigins, backendSpecsActions]);

  useEffect(() => {
    if (selectedSpecId) {
      loadShareLinks(selectedSpecId);
      loadCorsConfig(selectedSpecId);
    } else {
      setShareLinks([]);
      setCorsOrigins('');
      setCorsExpanded(false);
    }
  }, [selectedSpecId, loadShareLinks, loadCorsConfig]);

  const handleCreate = useCallback(async (e) => {
    e.preventDefault();
    if (!selectedSpecId || !backendSpecsActions?.createShareLink) return;
    setCreating(true);
    setError(null);
    try {
      const expiresAt = newExpiresAt ? new Date(newExpiresAt).toISOString() : null;
      await backendSpecsActions.createShareLink(Number(selectedSpecId), newLabel.trim() || null, expiresAt);
      setNewLabel('');
      setNewExpiresAt('');
      await loadShareLinks(selectedSpecId);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to create share link');
    } finally {
      setCreating(false);
    }
  }, [selectedSpecId, newLabel, newExpiresAt, backendSpecsActions, loadShareLinks]);

  const handleRevoke = useCallback(async (linkId) => {
    if (!backendSpecsActions?.revokeShareLink) return;
    setError(null);
    try {
      await backendSpecsActions.revokeShareLink(linkId);
      await loadShareLinks(selectedSpecId);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to revoke share link');
    }
  }, [selectedSpecId, backendSpecsActions, loadShareLinks]);

  const handleCopyUrl = useCallback(async (token, linkId) => {
    const baseUrl = backendSpecsSelectors?.selectBackendBaseUrl?.() || '/api';
    const url = `${baseUrl}/shared/${token}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(linkId);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      // Fallback: prompt user with selectable text
      window.prompt('Copy this URL:', url);
    }
  }, [backendSpecsSelectors]);

  const handleClose = useCallback(() => {
    setSelectedSpecId('');
    setShareLinks([]);
    setError(null);
    setNewLabel('');
    setNewExpiresAt('');
    setCorsOrigins('');
    setCorsExpanded(false);
    onClose();
  }, [onClose]);

  const isExpired = (expiresAt) => {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
  };

  const getStatusLabel = (link) => {
    if (link.revoked) return 'Revoked';
    if (isExpired(link.expiresAt)) return 'Expired';
    return 'Active';
  };

  const getStatusClass = (link) => {
    if (link.revoked) return 'share-link-status--revoked';
    if (isExpired(link.expiresAt)) return 'share-link-status--expired';
    return 'share-link-status--active';
  };

  if (!isOpen || !Modal) return null;

  return (
    <Modal isOpen={isOpen} contentLabel="Share Links">
      {ModalHeader && ModalTitle && (
        <ModalHeader>
          <ModalTitle>Share Links</ModalTitle>
        </ModalHeader>
      )}
      {ModalBody && (
        <ModalBody>
          <div className="share-links-container">
            <div className="share-links-selector">
              <label className="share-links-selector__label">
                <span>Select Spec:</span>
                <select
                  value={selectedSpecId}
                  onChange={(e) => setSelectedSpecId(e.target.value)}
                  className="share-links-selector__dropdown"
                >
                  <option value="">-- Choose a spec --</option>
                  {specsList.map((spec) => (
                    <option key={spec.id} value={String(spec.id)}>
                      {spec.name}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {error && (
              <div className="share-links-error">{error}</div>
            )}

            {selectedSpecId && (
              <>
                <div className="cors-settings">
                  <button
                    type="button"
                    className="cors-settings__toggle"
                    onClick={() => setCorsExpanded(!corsExpanded)}
                  >
                    <span className={`cors-settings__arrow ${corsExpanded ? 'cors-settings__arrow--expanded' : ''}`}>&#9654;</span>
                    CORS Settings
                  </button>
                  {corsExpanded && (
                    <div className="cors-settings__body">
                      <p className="cors-settings__info">
                        Allowed origins for this spec's shared links. One origin per line (e.g. https://example.com).
                        Leave empty to allow all origins.
                      </p>
                      <textarea
                        className="cors-settings__textarea"
                        rows={4}
                        placeholder={"https://example.com\nhttps://editor.swagger.io"}
                        value={corsOrigins}
                        onChange={(e) => setCorsOrigins(e.target.value)}
                        disabled={corsLoading || corsSaving}
                      />
                      <button
                        type="button"
                        className="btn btn-sm cors-settings__save"
                        onClick={handleSaveCors}
                        disabled={corsLoading || corsSaving}
                      >
                        {corsSaving ? 'Saving...' : 'Save CORS'}
                      </button>
                    </div>
                  )}
                </div>

                <form className="share-links-create-form" onSubmit={handleCreate}>
                  <input
                    type="text"
                    className="share-links-create-input"
                    placeholder="Label (optional, e.g. 'For QA team')"
                    value={newLabel}
                    onChange={(e) => setNewLabel(e.target.value)}
                    disabled={creating}
                  />
                  <input
                    type="datetime-local"
                    className="share-links-create-input share-links-create-input--date"
                    placeholder="Expires at (optional)"
                    value={newExpiresAt}
                    onChange={(e) => setNewExpiresAt(e.target.value)}
                    disabled={creating}
                  />
                  <button
                    type="submit"
                    className="btn btn-primary btn-sm"
                    disabled={creating}
                  >
                    {creating ? 'Creating...' : 'Create Link'}
                  </button>
                </form>

                {loading ? (
                  <div className="share-links-loading">Loading share links...</div>
                ) : shareLinks.length === 0 ? (
                  <div className="share-links-empty">No share links for this spec.</div>
                ) : (
                  <div className="share-links-table-wrapper">
                    <table className="share-links-table">
                      <thead>
                        <tr>
                          <th>Label</th>
                          <th>Token</th>
                          <th>Created By</th>
                          <th>Created At</th>
                          <th>Expires At</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {shareLinks.map((link) => (
                          <tr key={link.id} className={link.revoked ? 'share-links-row--revoked' : ''}>
                            <td>{link.label || '-'}</td>
                            <td className="share-links-token">
                              <code title={link.token}>
                                {link.token.substring(0, 8)}...
                              </code>
                            </td>
                            <td>{link.createdBy || '-'}</td>
                            <td>{link.createdAt ? new Date(link.createdAt).toLocaleString() : '-'}</td>
                            <td>{link.expiresAt ? new Date(link.expiresAt).toLocaleString() : 'Never'}</td>
                            <td>
                              <span className={`share-link-status ${getStatusClass(link)}`}>
                                {getStatusLabel(link)}
                              </span>
                            </td>
                            <td className="share-links-actions">
                              <button
                                type="button"
                                className="btn btn-sm btn-copy"
                                onClick={() => handleCopyUrl(link.token, link.id)}
                                title="Copy share URL"
                              >
                                {copiedId === link.id ? 'Copied!' : 'Copy URL'}
                              </button>
                              {!link.revoked && !isExpired(link.expiresAt) && (
                                <button
                                  type="button"
                                  className="btn btn-danger btn-sm"
                                  onClick={() => handleRevoke(link.id)}
                                >
                                  Revoke
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>
        </ModalBody>
      )}
      {ModalFooter && (
        <ModalFooter>
          <button type="button" className="btn btn-primary" onClick={handleClose}>
            Close
          </button>
        </ModalFooter>
      )}
    </Modal>
  );
};

ShareLinksDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  getComponent: PropTypes.func.isRequired,
  backendSpecsActions: PropTypes.object,
  backendSpecsSelectors: PropTypes.object,
};

export default ShareLinksDialog;
